import React from 'react';
import LongText from '../../../Elements/LongText';
import FaIcon from '../../../Elements/FaIcon';
import moneyHelper from '../../../Helpers/moneyHelper';

// One billed TpJob behind a platform invoice / summary row (CD-5294 drill-down). The
// list is a live re-query, so it can show MORE jobs than the frozen Bravo Shop Count
// when a later billing run stamped extra jobs into the period — that difference is the
// reconciliation signal, not a bug.
export default class TpPlatformInvoiceBravoJobItem {
  constructor(item) {
    if (item === undefined) {
      this.TpJobId = '';
      this.ThirdPartyJobId = '';
      this.ClientName = '';
      this.OfficeName = '';
      this.SurveyName = '';
      this.LocationName = '';
      this.Status = '';
      this.DateAssigned = null;
      this.PlatformCost = '';
      this.CurrencyCode = '';
      this.PricingSmCost = '';
      this.IsPricingSmCostOverridden = false;
      this.IsOutsourced = false;
      return;
    }
    this.TpJobId = item.tpJobId;
    this.ThirdPartyJobId = item.thirdPartyJobId ? item.thirdPartyJobId : '';
    this.ClientName = item.clientName ? item.clientName : '';
    this.OfficeName = item.officeName ? item.officeName : '';
    this.SurveyName = item.surveyName ? item.surveyName : '';
    this.LocationName = item.locationName ? item.locationName : '';
    this.Status = item.status ? item.status : '';
    this.DateAssigned = item.dateAssigned;
    this.PlatformCost = item.platformCost ?? '';
    this.CurrencyCode = item.currencyCode ? item.currencyCode : '';
    this.PricingSmCost = item.pricingSmCost ?? '';
    this.IsPricingSmCostOverridden = item.isPricingSmCostOverridden === true;
    this.IsOutsourced = item.isOutsourced === true;
  }

  static displayedInList = 'TpJobId';

  static NameOfTheIdField() {
    return 'TpJobId';
  }

  static nameOfTheItemDetailList() {
    return 'Bravo Job';
  }

  // Both costs are in the job's stamped platform currency, printed next to each amount — the grid
  // therefore has no separate Currency column (the Excel export still keeps one).
  static MoneyFields = ['PricingSmCost', 'PlatformCost'];
  static Currency() {
    return 'CurrencyCode';
  }
  static MoneyFieldsSecondary = [];
  static CurrencySecondary() {
    return '';
  }

  static DateFields = [];
  static BoolFields = [];

  static FieldsToShowInListTable = [];

  static formatDate(date) {
    if (!date) return '';
    return new Date(date).toLocaleDateString();
  }

  // Our pricing-side SM cost next to the platform's charge; the pen marks a manual
  // pricing-level override (SMCostPerEvalOverride). The override marker needs a renderer, and a
  // renderer opts the cell out of the MoneyFields path, so the amount is formatted here with the
  // same helper the machinery uses.
  static getPricingCostCellContent(item) {
    if (item.PricingSmCost === '') {
      return '';
    }
    const formattedCost = moneyHelper.toMoneyFormat(item.PricingSmCost, item.CurrencyCode);
    if (!item.IsPricingSmCostOverridden) {
      return formattedCost;
    }
    return (
      <span title='Pricing SM cost set manually on the pricing detail (override)'>
        {formattedCost}
        <FaIcon
          iconName='fa-solid fa-user-pen'
          innerClassName='text-warning'
          tooltipText='Pricing SM cost set manually on the pricing detail (override)'
        />
      </span>
    );
  }

  static FieldsToShowInDataTable() {
    return [
      {
        label: 'Client',
        orderById: 'ClientName',
        data: 'ClientName',
        className: 'text-left',
        minWidth: 80,
      },
      {
        label: 'Office',
        orderById: 'OfficeName',
        data: 'OfficeName',
        className: 'text-left',
        minWidth: 60,
      },
      {
        label: 'Bravo Job ID',
        orderById: 'TpJobId',
        data: 'TpJobId',
        className: 'text-center',
        minWidth: 40,
      },
      {
        label: 'TP Job ID',
        orderById: '',
        data: 'ThirdPartyJobId',
        className: 'text-center',
        minWidth: 40,
      },
      {
        label: 'Survey',
        orderById: 'SurveyName',
        data: (d) => (
          <LongText
            text={d.SurveyName}
            maxLength={35}
          />
        ),
        className: 'text-left',
        minWidth: 90,
      },
      {
        label: 'Location',
        orderById: 'LocationName',
        data: (d) => (
          <LongText
            text={d.LocationName}
            maxLength={35}
          />
        ),
        className: 'text-left',
        minWidth: 90,
      },
      {
        label: 'Status',
        orderById: 'Status',
        data: 'Status',
        className: 'text-center',
        minWidth: 40,
      },
      {
        // No orderById: TpJob.IsOutsourced is not in the repository's ordering switch.
        label: 'Outsourced',
        orderById: '',
        data: (item) => (item.IsOutsourced ? 'Yes' : 'No'),
        className: 'text-center',
        minWidth: 40,
      },
      {
        label: 'Date Assigned',
        orderById: 'DateAssigned',
        data: (item) => TpPlatformInvoiceBravoJobItem.formatDate(item.DateAssigned),
        className: 'text-center',
        minWidth: 50,
      },
      {
        label: 'Pricing Cost',
        orderById: '',
        data: (item) => TpPlatformInvoiceBravoJobItem.getPricingCostCellContent(item),
        className: 'text-right',
        minWidth: 40,
      },
      {
        label: 'Platform Cost',
        orderById: '',
        data: 'PlatformCost',
        className: 'text-right',
        minWidth: 40,
      },
    ];
  }
}
